// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable
} from '../../../../shared/services/claim-shared-helper';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../../shared/components/claim-fields/labeled-key-value-dropdown';
import MassChannelPdiClaim from '@deere/cps-it.warranty-model-factory/lib/claim-mass-channel-pdi';
import ResponsibleAccounts from '../../../../shared/components/claim-fields/responsible-accounts';
import {isValidDate} from '../../../../../shared/services/build-date';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updateMassChannelPdiClaimInputOnBlur(property, value);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? date.target.value : null;

    this.actions.updateMassChannelPdiClaimInputOnBlur(property, modifiedDate);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts);

}

function handleRetailerNameChange(event) {

    this.actions.updateRetailerNameDropDown(event.target.value, this.retailerNameCollection);

}

function handleDropDownBlur(property, event) {

    this.actions.updateMassChannelPdiClaimInputOnBlur(property, event.target.value);

}

function handleClaimPropertyChange(property, value) {

    this.actions.updateMassChannelPdiClaimProperty(property, value);

}

function handleClaimReferenceInfoChange(property, value) {

    this.actions.updateMassChannelPdiClaimReferenceInfoProperty(property, value);

}

function handleGoodsInstallationVatChange(event) {

    this.actions.updateGoodsInstallationVatDropDown(event.target.value, this.goodsInstallationVatOptions);

}

const canDisplayPurchaseOrder = key => [
    '0002000001',
    '0002000004'
].some(retailerKey => retailerKey === key);

const canDisplayWorkOrderThree = key => [
    '0002000001'
].some(retailerKey => retailerKey === key);

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <ResponsibleAccounts
                dealers={props.selectedAccounts}
                errorMessage='DEALER_LOCATION_IS_REQUIRED'
                highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                isEditable={canEditResponsibleAccount}
                isValid={props.formValidation.responsibleAccount}
                onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                onOptionChange={props::handleResponsibleAccountChange}
                selectedAccount={props.claim.responsibleAccount}
                vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
            />
            <div className='retailer-information'>
                <LabeledKeyValueDropDown
                    className='retailer-name'
                    errorMessage='RETAILER_NAME_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'retailerName', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.retailerName}
                    label='RETAILER_NAME'
                    list={props.retailerNameCollection}
                    onBlur={handleDropDownBlur.bind(props, 'retailerName')}
                    onChange={props::handleRetailerNameChange}
                    selectedValue={props.claim.retailerName}
                    vsrMessage={getVsrMessage(props.claim.errors, 'retailerName', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='store-number'
                    errorMessage='STORE_NUMBER_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'storeNumber', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.storeNumber}
                    label='STORE'
                    maxLength={10}
                    onBlur={handleInputBlur.bind(props, 'storeNumber')}
                    onChange={handleClaimPropertyChange.bind(props, 'storeNumber')}
                    value={props.claim.storeNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'storeNumber', canEditResponsibleAccount)}
                />
                <LabeledDateInput
                    className='setup-date'
                    date={props.claim.setupDate}
                    errorMessage='SETUP_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'setupDate', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.setupDate}
                    label='SETUP'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'setupDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'setupDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'setupDate', canEditResponsibleAccount)}
                />
                <LabeledDateInput
                    className='approval-date'
                    date={props.claim.approvalDate}
                    errorMessage={props.claim.approvalDate === null ? 'APPROVED_IS_REQUIRED' : 'APPROVED_DATE_CANNOT_BE_BEFORE_SETUP_DATE'}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'approvalDate', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.approvalDate && isRepairDateAfterFailureDate(props.claim.approvalDate, props.claim.setupDate)}
                    label='APPROVED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'approvalDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'approvalDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'approvalDate', canEditResponsibleAccount)}
                />
            </div>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={40}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={handleClaimReferenceInfoChange.bind(props, 'referenceNumber1')}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={handleClaimReferenceInfoChange.bind(props, 'referenceNumber2')}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                {canDisplayWorkOrderThree(props.claim.retailerName.key) &&
                <LabeledInputWithBlur
                    className='work-order-three'
                    errorMessage='WORK_ORDER_THREE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderThree', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderThree}
                    label='WORK_ORDER_THREE'
                    maxLength={10}
                    onBlur={handleInputBlur.bind(props, 'workOrderThree')}
                    onChange={handleClaimReferenceInfoChange.bind(props, 'referenceNumber3')}
                    value={props.claim.referenceInfo.referenceNumber3}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderThree', props.isEditable)}
                />
                }
                {canDisplayPurchaseOrder(props.claim.retailerName.key) &&
                <LabeledInputWithBlur
                    className='purchase-order'
                    errorMessage='PURCHASE_ORDER_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'purchaseOrder', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.purchaseOrder}
                    label='PURCHASE_ORDER'
                    maxLength={35}
                    onBlur={handleInputBlur.bind(props, 'purchaseOrder')}
                    onChange={handleClaimPropertyChange.bind(props, 'purchaseOrderNumber')}
                    value={props.claim.purchaseOrderNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'purchaseOrder', props.isEditable)}
                />
                }
            </div>
            {props.claim.showGoodsInstallationVat &&
                <LabeledKeyValueDropDown
                    className='goods-installation-vat'
                    errorMessage='GOODS_INSTALLATION_VAT_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={true}
                    label='GOODS_INSTALLATION_VAT'
                    list={props.goodsInstallationVatOptions}
                    onChange={props::handleGoodsInstallationVatChange}
                    selectedValue={props.claim.goodsInstallationVat}
                    vsrMessage={getVsrMessage(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                />
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(MassChannelPdiClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    goodsInstallationVatOptions: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    retailerNameCollection: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/marketing/mass-channel-pdi/components/claim-details-section/claim-details-container.js